import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import { getMyInfoAPI } from "../../../services/portal/student/studentService";
import { showErrorToastMsg } from "../../../utility/ToastMessage";
import Loader from "../../../components/Loader";
import StudentDetailsBox from "../../../components/portal/StudentDetailsBox";
import StudentDetailsManage from "../../../components/portal/StudentDetailsManage";
import StudentResetPasswordManage from "../../../components/portal/StudentResetPasswordManage";
import StudentPaymentHistory from "../../../components/portal/StudentPaymentHistory";
import { useAuth } from "../../../contexts/AuthContext";

const ProfilePage = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(1);
  const [loading, setLoading] = useState(false);
  const [student, setStudent] = useState(null);

  const loadStudentDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyInfoAPI();
      if (res && res.IsSuccessful && res.Data) {
        const studentData = res.Data;
        setStudent(studentData);
      } else {
        showErrorToastMsg("Student not found");
      }
    } catch (error) {
      console.error("Error loading student details:", error);
      showErrorToastMsg("Failed to load student details");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadStudentDetails();
    }
  }, [user, loadStudentDetails]);

  if (loading && !student) {
    return (
      <DashboardLayout
        title="Student Details"
        subtitle="Loading student information"
      >
        <div className="flex justify-center items-center py-12">
          <Loader />
        </div>
      </DashboardLayout>
    );
  }

  if (!student) {
    return (
      <DashboardLayout title="Student Details" subtitle="Student not found">
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Student not found</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Student Details">
      <div>
        <StudentDetailsBox studentDetails={student} />

        {/* Tabs */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab(1)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 1
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Personal & Parent Details
              </button>
              <button
                onClick={() => setActiveTab(2)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 2
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Payment History
              </button>
              <button
                onClick={() => setActiveTab(3)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 3
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Reset Password
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Tab 1: Personal & Parent Details */}
            {activeTab === 1 && <StudentDetailsManage studentData={student} />}

            {/* Tab 2: Payment History */}
            {activeTab === 2 && (
              <StudentPaymentHistory student={student.UserId} />
            )}

            {/* Tab 3: Reset Password */}
            {activeTab === 3 && (
              <StudentResetPasswordManage student={student} />
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;
